import {Injectable, OnDestroy} from "@angular/core";
import {configKey, SystemConfigurationValues} from "../../../common/configuration/services/system.configuration.utils";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {Status} from "../../lookups/lookups.types";
import {AsyncSubject, Subject} from "rxjs";
import {ShellInitHelper} from "../../../app/shell/services/ShellInitHelper";
import {PatientRecordStatus} from "../patient.record/models/patient.record.status";
import IStatus = Status.Status;
import StatusDS = Status.StatusDS;

export type StatusType = "ORDER"|"SPS"|"PPS";

@UntilDestroy()
@Injectable({ providedIn: 'root' })
  export class ClinicalStatusService implements  OnDestroy{

	statusTable: Array<IStatus>;
	_cancelledStatusKeys: Array<number>;

	private dataSubject = new AsyncSubject<Status.Status[]>();

	get cancelledStatusKeys(): Array<number> {
		if (!this._cancelledStatusKeys) {
			this._cancelledStatusKeys = this.systemCfg.getValidatedString('CancelledStatusKeys', '')
				.split(',').map(x => +(x.trim()));
		}

		return this._cancelledStatusKeys;
	}

	constructor(private systemCfg: SystemConfigurationValues,
				private shellInitHelper:ShellInitHelper) {
		this.shellInitHelper.StatusLookup()
			.pipe(untilDestroyed(this))
			.subscribe((res: StatusDS) => {
				this.statusTable = res.status;
				this.dataSubject.next(this.statusTable);
				this.dataSubject.complete();
			});
	}

	public getStatusDescription(statusKey:number){
		return this.statusTable.find(x=>x.statusKey===statusKey)?.description;
	}

	public statusTableObservable(){
		return this.dataSubject.asObservable();
	}

	public getStatusType(statusKey: number):StatusType{
		return <StatusType>this.statusTable.find((row) => row['statusKey'] === statusKey).type;
	}

	public getStatusByStatusKey(statusKey: number): IStatus {
		let statusRow = this.statusTable.find((row) => row['statusKey'] === statusKey);
		return statusRow;
	}

	public getPatientRecordStatusByStatusKey(statusKey: number): PatientRecordStatus{
		if (this.isExamDoneStatusKey(statusKey)){
			return PatientRecordStatus.ExamDone;
		} else if (this.isDictatedStatusKey(statusKey)) {
			return PatientRecordStatus.Dictated;
		} else if (this.isTranscribedStatusKey(statusKey)) {
			return PatientRecordStatus.Transcribed;
		} else if (this.isNRRStatusKey(statusKey)) {
			return PatientRecordStatus.NRR;
		} else if (this.isSigned1StatusKey(statusKey)) {
			return PatientRecordStatus.Signed1;
		} else if (this.isSigned2StatusKey(statusKey)) {
			return PatientRecordStatus.Signed2;
		} else if (this.isSigned3StatusKey(statusKey)) {
			return PatientRecordStatus.Signed3;
		} else if (this.isApprovedStatusKey(statusKey)) {
			return PatientRecordStatus.Approved;
		} else if (this.isCanceledStatusKey(statusKey)) {
			return PatientRecordStatus.Canceled;
		} else if (this.isInProgressStatusKey(statusKey)) {
			return PatientRecordStatus.InProgress;
		} else if (this.isArrivedStatusKey(statusKey)) {
			return PatientRecordStatus.Arrived;
		} else if (this.isDiscontinuedStatusKey(statusKey)) {
			return PatientRecordStatus.Discontinued;
		} else if (this.isScheduledStatusKey(statusKey)) {
			return PatientRecordStatus.Scheduled;
		} else if (this.isRequestedSignedStatusKey(statusKey)) {
			return PatientRecordStatus.RequestedSigned;
		} else if (this.isRequestedStatusKey(statusKey)) {
			return PatientRecordStatus.Requested;
		} else {
			return PatientRecordStatus.Undefined;
		}
	}

	public getCoreStatusKey(statusKey: number): number {

		let status = this.getStatusByStatusKey(statusKey);
		return status && status.coreStatus ?  status.coreStatus : statusKey;
	}

	public isCanceledStatusKey(statusKey: number) : boolean {
		statusKey = this.getCoreStatusKey(statusKey);
		return this.cancelledStatusKeys.includes(statusKey);
	}

	public isCancelledClinicalObject(object:{statusKey:number}) : boolean {
		return this.isCanceledStatusKey(object.statusKey);
	}

	public isInProgressStatusKey(statusKey: number): boolean {
		return this.isConfigurationStatus(statusKey, 'InProgressStatusKey');
	}

	public isScheduledStatusKey(statusKey: number): boolean {
		return this.isConfigurationStatus(statusKey, 'ScheduledStatusKey');
	}

	public isRequestedSignedStatusKey(statusKey: number): boolean {
		return this.isConfigurationStatus(statusKey, 'RequestedSignedStatusKey');
	}

	public isRequestedStatusKey(statusKey: number): boolean {
		return this.isConfigurationStatus(statusKey, 'RequestedStatusKey');
	}

	public isArrivedStatusKey(statusKey: number): boolean {
		return this.isConfigurationStatus(statusKey, 'ArrivedStatusKey');
	}

	public isDiscontinuedStatusKey(statusKey: number): boolean {
		return this.isConfigurationStatus(statusKey, 'DiscontinuedStatusKey');
	}

	public isExamDoneStatusKey(statusKey: number) {
		return this.isConfigurationStatus(statusKey, 'ExamDoneStatusKey');
	}

	public isDictatedStatusKey(statusKey: number) {
		return this.isConfigurationStatus(statusKey, 'DictatedStatusKey');
	}

	public isTranscribedStatusKey(statusKey: number) {
		return this.isConfigurationStatus(statusKey, 'TranscribedStatusKey');
	}

	public isNRRStatusKey(statusKey: number) {
		// TEMP!!!!!!!!!!!
		return false;
		//return this.isConfigurationStatus(statusKey, 'NRRStatusKey');
	}

	public isSigned1StatusKey(statusKey: number) {
		return this.isConfigurationStatus(statusKey, 'Signed1StatusKey');
	}

	public isSigned2StatusKey(statusKey: number) {
		return this.isConfigurationStatus(statusKey, 'Signed2StatusKey');
	}

	public isSigned3StatusKey(statusKey: number) {
		return this.isConfigurationStatus(statusKey, 'Signed3StatusKey');
	}

	public isApprovedStatusKey(statusKey: number) {
		return this.isConfigurationStatus(statusKey, 'ApprovedStatusKey');
	}

	ngOnDestroy(): void {
		this.dataSubject.complete();
	}

	private isConfigurationStatus(statusKey: number, statusConfigurationKey: configKey){
		statusKey = this.getCoreStatusKey(statusKey);
		let configStatusKey = this.systemCfg.getNumber(statusConfigurationKey);
		return statusKey === configStatusKey;
	}

	public getStatusKeyByPatientRecordStatus(patientRecordStatus: PatientRecordStatus) {
		switch (patientRecordStatus) {
			case PatientRecordStatus.Undefined:
				break;
			case PatientRecordStatus.InProgress:
				return this.systemCfg.getNumber('InProgressStatusKey');
			case PatientRecordStatus.Arrived:
				return this.systemCfg.getNumber('ArrivedStatusKey');
			case PatientRecordStatus.Discontinued:
				return this.systemCfg.getNumber('DiscontinuedStatusKey');
			case PatientRecordStatus.Scheduled:
				return this.systemCfg.getNumber('ScheduledStatusKey');
			case PatientRecordStatus.RequestedSigned:
				return this.systemCfg.getNumber('RequestedSignedStatusKey');
			case PatientRecordStatus.Requested:
				return this.systemCfg.getNumber('RequestedStatusKey');
			case PatientRecordStatus.ExamDone:
				return this.systemCfg.getNumber('ExamDoneStatusKey');
			case PatientRecordStatus.Dictated:
				return this.systemCfg.getNumber('DictatedStatusKey');
			case PatientRecordStatus.Transcribed:
				return this.systemCfg.getNumber('TranscribedStatusKey');
			case PatientRecordStatus.NRR:
				return this.systemCfg.getNumber('NRRStatusKey'); 
			case PatientRecordStatus.Signed1:
				return this.systemCfg.getNumber('Signed1StatusKey');
			case PatientRecordStatus.Signed2:
				return this.systemCfg.getNumber('Signed2StatusKey');
			case PatientRecordStatus.Signed3:
				return this.systemCfg.getNumber('Signed3StatusKey');
			case PatientRecordStatus.Approved:
				return this.systemCfg.getNumber('ApprovedStatusKey');
			case PatientRecordStatus.Canceled:
				return this.systemCfg.getNumber('CancelledStatusKey');
			default:
				return PatientRecordStatus.Undefined
		}
	}
}
